import React, { useState, useRef } from 'react';
import {
  Building2, Mail, Phone, Globe, MapPin, Upload, Trash2, Shield,
  TrendingUp, DollarSign, Users, Award, AlertCircle, CheckCircle,
  Target, Star, BarChart3, Linkedin, Twitter, Facebook
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { NAICSSelector } from './NAICSSelector';
import { SetAsideSelector } from './SetAsideSelector';
import { supabase } from '../../lib/supabase';
import { US_STATES } from '../../lib/constants';
import { SEED_ABOUT_US } from '../../lib/export/jobDescriptionNormalize';
import toast from 'react-hot-toast';

interface BusinessAddress {
  street1: string;
  street2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

export function CompanyInformation() {
  const { profile, updateProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Enhanced: Add more fields to the existing formData structure
  const [formData, setFormData] = useState({
    companyName: profile?.company_name || '',
    aboutUs: profile?.about_us || SEED_ABOUT_US,
    legalStructure: profile?.legal_structure || '',
    yearEstablished: profile?.year_established ? String(profile.year_established) : '',
    dunsNumber: profile?.duns_number || '',
    ueiNumber: profile?.uei_number || '',
    cageCode: profile?.cage_code || '',
    samStatus: profile?.sam_status || '',
    email: profile?.email || '',
    phone: profile?.phone || '',
    website: profile?.website_url || '',
    naicsCodes: profile?.naics_codes || [],
    qualifiedSetAsides: profile?.qualified_set_asides || [],
    businessAddress: {
      street1: profile?.business_address?.street1 || '',
      street2: profile?.business_address?.street2 || '',
      city: profile?.business_address?.city || '',
      state: profile?.business_address?.state || '',
      zip: profile?.business_address?.zip || '',
      country: 'United States'
    } as BusinessAddress,
    
    // Enhanced fields - only add new ones that don't conflict
    totalEmployees: profile?.total_employees || 0,
    annualRevenue: profile?.annual_revenue || 0,
    bondingCapacity: profile?.bonding_capacity || 0,
    securityClearanceLevel: profile?.security_clearance_level || 'none',
    facilityClearanceLevel: profile?.facility_clearance_level || 'none',
    // Social links
    linkedinUrl: profile?.linkedin_url || '',
    twitterUrl: profile?.twitter_url || '',
    facebookUrl: profile?.facebook_url || ''
  });

  // Enhanced: Calculate profile completeness
  const calculateCompleteness = () => {
    const requiredFields = [
      formData.companyName,
      formData.legalStructure,
      formData.yearEstablished,
      formData.email,
      formData.businessAddress.city,
      formData.businessAddress.state
    ];
    
    const arrayFields = [
      formData.naicsCodes,
      formData.qualifiedSetAsides
    ];
    
    const filledFields = requiredFields.filter(field => field && field !== '').length;
    const filledArrays = arrayFields.filter(arr => arr && arr.length > 0).length;
    
    return Math.round(((filledFields + filledArrays) / (requiredFields.length + arrayFields.length)) * 100);
  };

  const completenessScore = calculateCompleteness();

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      if (!event.target.files || event.target.files.length === 0) return;

      setUploadingLogo(true);
      const file = event.target.files[0];
      const fileExt = file.name.split('.').pop();
      const filePath = `${profile?.id}/logo.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('company-logos')
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('company-logos')
        .getPublicUrl(filePath);

      await updateProfile({ company_logo_url: publicUrl });
      toast.success('Company logo updated successfully');
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = async () => {
    try {
      if (!profile?.company_logo_url) return;

      // Files are stored at `${profile.id}/logo.<ext>`. The previous code removed only the
      // bare filename, which doesn't match the stored path → the file was orphaned in
      // storage. Reconstruct the full object path. Storage cleanup is best-effort so the
      // logo is always cleared from the profile even if the file removal hiccups.
      const basename = profile.company_logo_url.split('/').pop();
      if (basename && profile?.id) {
        const { error } = await supabase.storage
          .from('company-logos')
          .remove([`${profile.id}/${basename}`]);
        if (error) console.warn('Logo storage removal warning:', error.message);
      }

      await updateProfile({ company_logo_url: null });
      toast.success('Company logo removed');
    } catch (error) {
      console.error('Error removing logo:', error);
      toast.error('Failed to remove logo');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      // Enhanced: Include new fields in the update
      await updateProfile({
        company_name: formData.companyName,
        about_us: formData.aboutUs,
        legal_structure: formData.legalStructure,
        year_established: formData.yearEstablished ? parseInt(formData.yearEstablished) : undefined,
        duns_number: formData.dunsNumber,
        uei_number: formData.ueiNumber,
        cage_code: formData.cageCode,
        sam_status: formData.samStatus,
        email: formData.email,
        phone: formData.phone,
        website_url: formData.website,
        naics_codes: formData.naicsCodes,
        qualified_set_asides: formData.qualifiedSetAsides,
        business_address: formData.businessAddress,
        // Enhanced fields
        total_employees: formData.totalEmployees || undefined,
        annual_revenue: formData.annualRevenue || undefined,
        bonding_capacity: formData.bondingCapacity || undefined,
        security_clearance_level: formData.securityClearanceLevel,
        facility_clearance_level: formData.facilityClearanceLevel,
        profile_completeness_score: completenessScore,
        // Social links
        linkedin_url: formData.linkedinUrl || null,
        twitter_url: formData.twitterUrl || null,
        facebook_url: formData.facebookUrl || null
      });
      toast.success('Company information updated successfully');
    } catch (error) {
      console.error('Error updating company information:', error);
      toast.error('Failed to update company information');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Enhanced: Add Profile Intelligence Dashboard */}
      <div className="bg-bw-surface rounded-xl p-6 border border-bw-border">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-bw-ink flex items-center">
            <BarChart3 className="h-5 w-5 mr-2 text-bw-primary" />
            Profile Intelligence
          </h3>
          <div className={`px-3 py-1 rounded-full text-sm font-medium ${
            completenessScore >= 80 ? 'text-success bg-success-bg' :
            completenessScore >= 60 ? 'text-fit-moderate bg-fit-moderate-bg' : 'text-urgent bg-urgent-bg'
          }`}>
            {completenessScore}% Complete
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-bw-surface-subtle rounded-lg p-4 border border-bw-border">
            <div className="flex items-center justify-between">
              <span className="text-sm text-bw-muted">Profile Score</span>
              <span className="text-lg font-bold text-bw-ink">{completenessScore}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
              <div 
                className={`h-2 rounded-full transition-all duration-300 ${
                  completenessScore >= 80 ? 'bg-fit-strong-stroke' :
                  completenessScore >= 60 ? 'bg-fit-moderate-stroke' : 'bg-fit-weak-stroke'
                }`}
                style={{ width: `${completenessScore}%` }}
              />
            </div>
          </div>
          
          <div className="bg-bw-surface-subtle rounded-lg p-4 border border-bw-border">
            <div className="flex items-center justify-between">
              <span className="text-sm text-bw-muted">Team Size</span>
              <span className="text-lg font-bold text-bw-ink">{formData.totalEmployees || 0}</span>
            </div>
            <div className="flex items-center mt-2 text-sm text-bw-muted">
              <Users className="h-4 w-4 mr-1" />
              Employees
            </div>
          </div>
          
          <div className="bg-bw-surface-subtle rounded-lg p-4 border border-bw-border">
            <div className="flex items-center justify-between">
              <span className="text-sm text-bw-muted">Clearance</span>
              <span className="text-sm font-bold text-bw-ink">
                {formData.securityClearanceLevel === 'none' ? 'None' : 
                 formData.securityClearanceLevel === 'public_trust' ? 'Public Trust' :
                 formData.securityClearanceLevel.replace('_', ' ').toUpperCase()}
              </span>
            </div>
            <div className="flex items-center mt-2 text-sm text-bw-muted">
              <Shield className="h-4 w-4 mr-1" />
              Security Level
            </div>
          </div>
        </div>

        {completenessScore < 80 && (
          <div className="mt-4 bg-fit-moderate-bg border border-fit-moderate-border rounded-lg p-4">
            <div className="flex items-start">
              <AlertCircle className="h-5 w-5 text-fit-moderate mt-0.5 mr-3" />
              <div>
                <h4 className="text-sm font-medium text-fit-moderate">Boost Your Profile</h4>
                <p className="text-sm text-fit-moderate mt-1">
                  Complete more sections to improve your proposal quality and win rates.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Original form with enhanced fields added */}
      <form onSubmit={handleSubmit} className="bg-bw-surface rounded-xl border border-bw-border p-4 sm:p-8">
        <h3 className="text-2xl font-bold mb-8 flex items-center text-bw-ink">
          <Building2 className="h-6 w-6 mr-3 text-bw-primary" />
          Company Information
        </h3>

        {/* Company Logo Section - Keep Original */}
        <div className="mb-8 p-4 sm:p-6 bg-bw-surface-subtle rounded-lg border border-bw-border">
          <h4 className="text-lg font-semibold mb-4">Company Logo</h4>
          <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
            <div className="w-32 h-32 rounded-lg border-2 border-dashed border-bw-border flex items-center justify-center bg-bw-surface overflow-hidden">
              {profile?.company_logo_url ? (
                <img
                  src={profile.company_logo_url}
                  alt="Company Logo"
                  className="w-full h-full object-contain"
                />
              ) : (
                <Building2 className="h-12 w-12 text-bw-muted" />
              )}
            </div>
            <div className="space-y-3 w-full sm:w-auto">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleLogoUpload}
                accept="image/*"
                className="hidden"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingLogo}
                className="w-full sm:w-auto flex items-center justify-center px-4 py-2 bg-bw-surface border border-bw-border rounded-md text-sm font-medium text-bw-ink hover:bg-bw-surface-subtle focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-bw-primary"
              >
                <Upload className="h-4 w-4 mr-2" />
                {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
              </button>
              {profile?.company_logo_url && (
                <button
                  type="button"
                  onClick={handleRemoveLogo}
                  className="w-full sm:w-auto flex items-center justify-center px-4 py-2 border border-red-300 rounded-md text-sm font-medium text-urgent hover:bg-urgent-bg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Remove Logo
                </button>
              )}
              <p className="text-sm text-bw-muted">
                Recommended: 400x400px or larger, PNG or JPG
              </p>
            </div>
          </div>
        </div>

        {/* Form Fields */}
        <div className="space-y-8">
          {/* Basic Information - Keep Original */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            <div className="col-span-1 md:col-span-2">
              <label className="block text-sm font-medium text-bw-ink">
                Company Name *
              </label>
              <input
                type="text"
                value={formData.companyName}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, companyName: e.target.value }))
                }
                className="mt-1 block w-full rounded-lg border border-bw-border bg-bw-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bw-primary/30"
                required
              />
            </div>

            <div className="col-span-1 md:col-span-2">
              <label className="block text-sm font-medium text-bw-ink">
                About Us
              </label>
              <p className="text-xs text-bw-muted mb-1">
                Pulled verbatim into generated job descriptions and other postings — never AI-rewritten.
              </p>
              <textarea
                rows={4}
                value={formData.aboutUs}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, aboutUs: e.target.value }))
                }
                className="mt-1 block w-full rounded-lg border border-bw-border bg-bw-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bw-primary/30"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-bw-ink">
                Legal Structure
              </label>
              <select
                value={formData.legalStructure}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, legalStructure: e.target.value }))
                }
                className="mt-1 block w-full rounded-lg border border-bw-border bg-bw-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bw-primary/30"
              >
                <option value="">Select structure</option>
                <option value="LLC">LLC</option>
                <option value="Corporation">Corporation</option>
                <option value="Partnership">Partnership</option>
                <option value="Sole Proprietorship">Sole Proprietorship</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-bw-ink">
                Year Established
              </label>
              <input
                type="number"
                min="1900"
                max={new Date().getFullYear()}
                value={formData.yearEstablished}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, yearEstablished: e.target.value }))
                }
                className="mt-1 block w-full rounded-lg border border-bw-border bg-bw-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bw-primary/30"
              />
            </div>

            {/* Enhanced: Add new fields */}
            <div>
              <label className="block text-sm font-medium text-bw-ink">
                Total Employees
              </label>
              <input
                type="number"
                min="0"
                value={formData.totalEmployees}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, totalEmployees: parseInt(e.target.value) || 0 }))
                }
                className="mt-1 block w-full rounded-lg border border-bw-border bg-bw-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bw-primary/30"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-bw-ink">
                Annual Revenue (USD)
              </label>
              <input
                type="number"
                min="0"
                step="1000"
                value={formData.annualRevenue}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, annualRevenue: parseFloat(e.target.value) || 0 }))
                }
                className="mt-1 block w-full rounded-lg border border-bw-border bg-bw-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bw-primary/30"
                placeholder="0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-bw-ink">
                Bonding Capacity (USD)
              </label>
              <input
                type="number"
                min="0"
                step="1000"
                value={formData.bondingCapacity}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, bondingCapacity: parseFloat(e.target.value) || 0 }))
                }
                className="mt-1 block w-full rounded-lg border border-bw-border bg-bw-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bw-primary/30"
                placeholder="0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-bw-ink">
                Security Clearance Level
              </label>
              <select
                value={formData.securityClearanceLevel}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, securityClearanceLevel: e.target.value }))
                }
                className="mt-1 block w-full rounded-lg border border-bw-border bg-bw-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bw-primary/30"
              >
                <option value="none">No Clearance Required</option>
                <option value="public_trust">Public Trust</option>
                <option value="confidential">Confidential</option>
                <option value="secret">Secret</option>
                <option value="top_secret">Top Secret</option>
                <option value="top_secret_sci">Top Secret/SCI</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-bw-ink">
                Facility Clearance Level
              </label>
              <select
                value={formData.facilityClearanceLevel}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, facilityClearanceLevel: e.target.value }))
                }
                className="mt-1 block w-full rounded-lg border border-bw-border bg-bw-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bw-primary/30"
              >
                <option value="none">No Facility Clearance</option>
                <option value="confidential">Confidential</option>
                <option value="secret">Secret</option>
                <option value="top_secret">Top Secret</option>
              </select>
            </div>

            {/* Federal entity identifiers — UEI is the primary federal ID (replaced DUNS in April 2022) */}
            <div>
              <label className="block text-sm font-medium text-bw-ink">
                UEI (Unique Entity ID)
              </label>
              <input
                type="text"
                maxLength={12}
                value={formData.ueiNumber}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, ueiNumber: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') }))
                }
                className="mt-1 block w-full rounded-lg border border-bw-border bg-bw-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bw-primary/30 font-mono uppercase"
                placeholder="12-character SAM.gov ID"
              />
              <p className="text-xs text-bw-muted mt-1">
                Your primary federal identifier (replaced DUNS in 2022). 12 alphanumeric characters from SAM.gov.
                {formData.ueiNumber && formData.ueiNumber.length !== 12 && (
                  <span className="text-fit-moderate"> — should be 12 characters.</span>
                )}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-bw-ink">
                CAGE Code
              </label>
              <input
                type="text"
                maxLength={5}
                value={formData.cageCode}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, cageCode: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') }))
                }
                className="mt-1 block w-full rounded-lg border border-bw-border bg-bw-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bw-primary/30 font-mono uppercase"
                placeholder="5-character code"
              />
              <p className="text-xs text-bw-muted mt-1">Commercial and Government Entity (CAGE) code — 5 characters.</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-bw-ink">
                DUNS Number <span className="text-bw-muted font-normal">(legacy)</span>
              </label>
              <input
                type="text"
                value={formData.dunsNumber}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, dunsNumber: e.target.value }))
                }
                className="mt-1 block w-full rounded-lg border border-bw-border bg-bw-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bw-primary/30 font-mono"
                placeholder="Optional — retired federally in 2022"
              />
              <p className="text-xs text-bw-muted mt-1">No longer used for federal registration; kept for legacy records only.</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-bw-ink">
                SAM.gov Status
              </label>
              <select
                value={formData.samStatus}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, samStatus: e.target.value }))
                }
                className="mt-1 block w-full rounded-lg border border-bw-border bg-bw-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bw-primary/30"
              >
                <option value="">Select status</option>
                <option value="active">Active</option>
                <option value="pending">Pending</option>
                <option value="expired">Expired</option>
              </select>
            </div>
          </div>

          {/* Contact Information - Keep Original */}
          <div className="border-t border-bw-border pt-6">
            <h4 className="text-lg font-medium mb-4">Contact Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <label className="block text-sm font-medium text-bw-ink">
                  Email Address
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-bw-muted" />
                  </div>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, email: e.target.value }))
                    }
                    className="block w-full pl-10 pr-3 py-2 rounded-lg border border-bw-border bg-bw-surface text-sm focus:outline-none focus:ring-2 focus:ring-bw-primary/30"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-bw-ink">
                  Phone Number
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Phone className="h-5 w-5 text-bw-muted" />
                  </div>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, phone: e.target.value }))
                    }
                    className="block w-full pl-10 pr-3 py-2 rounded-lg border border-bw-border bg-bw-surface text-sm focus:outline-none focus:ring-2 focus:ring-bw-primary/30"
                  />
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-bw-ink">
                  Website
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Globe className="h-5 w-5 text-bw-muted" />
                  </div>
                  <input
                    type="url"
                    value={formData.website}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, website: e.target.value }))
                    }
                    className="block w-full pl-10 pr-3 py-2 rounded-lg border border-bw-border bg-bw-surface text-sm focus:outline-none focus:ring-2 focus:ring-bw-primary/30"
                    placeholder="https://"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Social Links Section */}
          <div className="border-t border-bw-border pt-6">
            <h4 className="text-lg font-medium mb-4">Company Social Links</h4>
            <p className="text-sm text-bw-muted mb-4">
              Add your company's social media profiles. These help establish credibility and can be included in proposals.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
              <div>
                <label className="block text-sm font-medium text-bw-ink">
                  LinkedIn
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Linkedin className="h-5 w-5 text-[#0077B5]" />
                  </div>
                  <input
                    type="url"
                    value={formData.linkedinUrl}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, linkedinUrl: e.target.value }))
                    }
                    className="block w-full pl-10 pr-3 py-2 rounded-lg border border-bw-border bg-bw-surface text-sm focus:outline-none focus:ring-2 focus:ring-bw-primary/30"
                    placeholder="https://linkedin.com/company/..."
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-bw-ink">
                  Twitter / X
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Twitter className="h-5 w-5 text-bw-ink" />
                  </div>
                  <input
                    type="url"
                    value={formData.twitterUrl}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, twitterUrl: e.target.value }))
                    }
                    className="block w-full pl-10 pr-3 py-2 rounded-lg border border-bw-border bg-bw-surface text-sm focus:outline-none focus:ring-2 focus:ring-bw-primary/30"
                    placeholder="https://twitter.com/..."
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-bw-ink">
                  Facebook
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Facebook className="h-5 w-5 text-[#1877F2]" />
                  </div>
                  <input
                    type="url"
                    value={formData.facebookUrl}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, facebookUrl: e.target.value }))
                    }
                    className="block w-full pl-10 pr-3 py-2 rounded-lg border border-bw-border bg-bw-surface text-sm focus:outline-none focus:ring-2 focus:ring-bw-primary/30"
                    placeholder="https://facebook.com/..."
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Business Address - Keep Original */}
          <div className="border-t border-bw-border pt-6">
            <h4 className="text-lg font-medium mb-4">Business Address</h4>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-bw-ink">
                  Street Address Line 1
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <MapPin className="h-5 w-5 text-bw-muted" />
                  </div>
                  <input
                    type="text"
                    value={formData.businessAddress.street1}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        businessAddress: {
                          ...prev.businessAddress,
                          street1: e.target.value,
                        },
                      }))
                    }
                    className="block w-full pl-10 pr-3 py-2 rounded-lg border border-bw-border bg-bw-surface text-sm focus:outline-none focus:ring-2 focus:ring-bw-primary/30"
                    placeholder="123 Main Street"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-bw-ink">
                  Street Address Line 2
                </label>
                <input
                  type="text"
                  value={formData.businessAddress.street2}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      businessAddress: {
                        ...prev.businessAddress,
                        street2: e.target.value,
                      },
                    }))
                  }
                  className="mt-1 block w-full rounded-lg border border-bw-border bg-bw-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bw-primary/30"
                  placeholder="Suite 100"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-bw-ink">
                    City
                  </label>
                  <input
                    type="text"
                    value={formData.businessAddress.city}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        businessAddress: {
                          ...prev.businessAddress,
                          city: e.target.value,
                        },
                      }))
                    }
                    className="mt-1 block w-full rounded-lg border border-bw-border bg-bw-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bw-primary/30"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-bw-ink">
                    State
                  </label>
                  <select
                    value={formData.businessAddress.state}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        businessAddress: {
                          ...prev.businessAddress,
                          state: e.target.value,
                        },
                      }))
                    }
                    className="mt-1 block w-full rounded-lg border border-bw-border bg-bw-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bw-primary/30"
                  >
                    <option value="">Select state</option>
                    {Object.entries(US_STATES).map(([code, name]) => (
                      <option key={code} value={code}>
                        {name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-bw-ink">
                    ZIP Code
                  </label>
                  <input
                    type="text"
                    value={formData.businessAddress.zip}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        businessAddress: {
                          ...prev.businessAddress,
                          zip: e.target.value,
                        },
                      }))
                    }
                    className="mt-1 block w-full rounded-lg border border-bw-border bg-bw-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bw-primary/30"
                    placeholder="12345"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* NAICS and Set-Asides - Keep Original */}
          <div className="border-t border-bw-border pt-6 space-y-6">
            {/* NAICS Selector Container */}
            <div className="relative overflow-visible">
              <NAICSSelector
                selectedCodes={formData.naicsCodes}
                onChange={(codes) =>
                  setFormData((prev) => ({
                    ...prev,
                    naicsCodes: codes,
                  }))
                }
                maxSelections={30}
              />
            </div>

            {/* Set-Asides Selector */}
            <div className="relative overflow-visible">
              <SetAsideSelector
                selectedSetAsides={formData.qualifiedSetAsides}
                onChange={(setAsides) =>
                  setFormData((prev) => ({
                    ...prev,
                    qualifiedSetAsides: setAsides,
                  }))
                }
              />
            </div>
          </div>

          {/* Submit Button - Keep Original */}
          <div className="flex justify-end pt-6 border-t border-bw-border">
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-bw-primary text-white rounded-lg hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-bw-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Saving...
                </span>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}